import { useState } from 'react';
import moment from 'moment';
import { flattenDeep } from 'lodash';
import { ISO_8601_DATE_FORMAT, MONTH_FORMAT_SHORT, MONTH_NUMBER_LONG, YEAR_MONTH_FORMAT } from '@model/common';
import { Offer } from '@model/iceberg/service/price-calendar/monthly-response';
import { TourAvailability, TourAvailabilityYears } from '@model/tours';
import { Calendar, Footer, Nav } from '@components/price-calendar';

export interface TourPricingCalendarProps {
  results: TourAvailabilityYears;
  initialYear: string;
  initialMonth: string;
  isLoading: boolean;
  cheapest: TourAvailability;
  onSubmit: (payload: TourAvailability) => void;
  isPrivateTours?: boolean;
}

export const TourPricingCalendar = ({
  results,
  initialYear,
  initialMonth,
  isLoading,
  cheapest,
  onSubmit,
  isPrivateTours
}: TourPricingCalendarProps) => {
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(moment().month(initialMonth).format(MONTH_NUMBER_LONG));
  const calendarMonth = `${year}-${month}`;

  const [selectedTour, setSelectedTour] = useState<any>(cheapest);
  const calendarDate = moment(selectedTour.departureDate).format(ISO_8601_DATE_FORMAT);

  const resultsByYear = Object.values(results);
  const monthRange = flattenDeep(
    resultsByYear.map((months) => {
      const hotelAvailability = months[0].months;
      return Object.values(hotelAvailability).map((monthPrices) =>
        moment(monthPrices[0].departureDate).format(YEAR_MONTH_FORMAT)
      );
    })
  ).sort();

  const prices = results[year]
    ? results[year][0].months[
        moment()
          .month(Number(month) - 1)
          .format(MONTH_FORMAT_SHORT)
      ]
    : [];

  const calendarPrices: Array<Offer> = (prices || []).map(({ departureDate, price, reference, soldOut }) => {
    return {
      date: moment(departureDate).format(ISO_8601_DATE_FORMAT),
      price,
      totalPrice: price,
      packageReferences: [`${reference}`],
      cheapest: reference === cheapest.reference,
      soldOut
    };
  });

  const onMonthChange = (month: string) => {
    const [newYear, newMonth] = month.split('-');
    if (newYear !== year) {
      setYear(newYear);
    }
    if (newMonth !== month) {
      setMonth(newMonth);
    }
  };
  const onDayChange = (date: string) => {
    const newSelectedTour = prices.find(({ departureDate }) => departureDate.indexOf(date) === 0);
    if (newSelectedTour) {
      setSelectedTour(newSelectedTour);
    } else if (isPrivateTours) {
      setSelectedTour({ departureDate: date });
    }
  };

  const handleOnSubmit = () => {
    if (selectedTour) {
      onSubmit(selectedTour);
    }
  };

  return (
    <>
      {monthRange.includes(calendarMonth) && (
        <Nav date={calendarMonth} range={monthRange} onChange={isLoading ? () => {} : onMonthChange} />
      )}
      <Calendar
        date={calendarDate}
        month={calendarMonth}
        prices={calendarPrices}
        onChange={onDayChange}
        loading={isLoading}
        disabled={isLoading}
        disableInvalid
        isPrivateTours={isPrivateTours}
      />
      <Footer
        date={selectedTour?.departureDate}
        hasPrices={!!calendarPrices.length}
        onSubmit={handleOnSubmit}
        duration={moment(selectedTour?.returnDate).diff(selectedTour?.departureDate, 'day')}
        buttonLabelKey={isPrivateTours ? 'common__label--continue' : 'common__label--continue-customise'}
      />
    </>
  );
};
